import React from 'react'
import UserLayout from '../../layouts/UserLayout'
import { useDispatch, useSelector } from 'react-redux'
import { useState, useCallback, useEffect } from 'react'
import { handleError } from '../../utils/error'
import { useNavigate } from 'react-router-dom'
import { currentUserActions } from '../../redux/store'
import { api } from '../../utils/api'
import { Box, Card, CircularProgress, Typography } from '@mui/material'
import { mainGreen } from '../../utils/color'
import axiosInstance from '../../services/apiBase'
import { enqueueSnackbar } from 'notistack'
import EditGuestOrderForm from '../../components/EditGuestOrderForm'
import GuestOrderItem from './GuestOrderItem'

const formatProductDetails = (order) => {
	if (order.source === 'zalo' || order.source === 'web') {
		return (
			order.line_items
				?.filter((item) => item.quantity != null)
				?.map((item) => `${item.quantity} x ${item.name}`)
				?.join('\n') || ''
		)
	}
	return order.productInfo
}

export default function GuestOrder() {
	const { currentUser } = useSelector((state) => state.currentUser)
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const [guestOrders, setGuestOrders] = useState([])
	const [loading, setLoading] = useState(true)
	const [orderEditing, setOrderEditing] = useState()

	const getGuestOrders = useCallback(
		async function () {
			try {
				const response = await axiosInstance({ url: api.GET_GUEST_ORDERS_STORE_MANAGER, method: 'GET' })
				setGuestOrders(response?.data || [])
				setLoading(false)
			} catch (err) {
				handleError(err)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[dispatch, navigate],
	)

	useEffect(() => {
		getGuestOrders()
	}, [getGuestOrders])

	const handleSubmit = async (orderData) => {
		createOrder(orderData)
	}

	const createOrder = async (orderData, otherData) => {
		if (!orderData._id || !currentUser.store) {
			enqueueSnackbar('Lỗi không tìm thấy store id', { variant: 'error' })
			return
		}
		if (!orderData.bill || !orderData.shippingTotal) {
			enqueueSnackbar('Lỗi không tìm thấy thông tin đơn hàng', { variant: 'error' })
			return
		}

		setLoading(true)
		try {
			const submitData = {
				quantity: orderData.quantity || '0',
				totalPrice: Number(typeof orderData.bill === 'string' ? orderData.bill.replace(/,/g, '') : orderData.bill),
				detail: formatProductDetails(orderData),
				payment: orderData.payment,
				app: 'false',
				quickPurchase: 'false',
				creator: currentUser.id,
				customerNote: `${orderData.name} - ${orderData.phone}\n${orderData.address}`,
				source: orderData.source || 'web',
				code: orderData.code,
				line_items: JSON.stringify(orderData.line_items),
				shippingTime: orderData.shippingTime,
				tag: JSON.stringify([]),
				store: currentUser.store,
				shippingTotal: Number(
					typeof orderData.shippingTotal === 'string'
						? orderData.shippingTotal.replace(/,/g, '')
						: orderData.shippingTotal,
				),
				note: orderData.note || '',
			}

			if (otherData.scheduleTime) {
				const formData = new FormData()

				formData.append(
					'data',
					JSON.stringify({
						createOrderData: {
							...submitData,
							creator: currentUser,
							tag: otherData.tags,
							quickPurchase: !!submitData.quickPurchase,
							app: !!submitData.app,
						},
						timer: otherData.scheduleTime,
					}),
				)

				if (otherData.image) {
					formData.append('image', otherData.image)
				}

				await axiosInstance({
					method: 'POST',
					headers: { 'Content-Type': 'multipart/form-data' },
					url: api.POST_CREATE_TIMER_ORDER,
					data: formData,
					withCredentials: true,
				})
			} else {
				const formData = new FormData()
				Object.keys(submitData).forEach((key) => {
					formData.append(key, submitData[key])
				})

				if (otherData.image) {
					formData.append('image', otherData.image)
				}

				await axiosInstance({
					method: 'POST',
					headers: { 'Content-Type': 'multipart/form-data' },
					url: api.POST_CREATE_ORDER,
					data: formData,
					withCredentials: true,
				})
			}

			await approveGuestOrder(orderData._id)
			await getGuestOrders()
			setOrderEditing(null)
			enqueueSnackbar('Đơn hàng đã được tạo thành công', { variant: 'success' })
		} catch (error) {
			handleError(error)
		} finally {
			setLoading(false)
		}
	}

	const approveGuestOrder = async (id) => {
		try {
			await axiosInstance({ url: api.APPROVE_GUEST_ORDERS_STORE_MANAGER, method: 'PUT', data: { id } })
			await getGuestOrders()
		} catch (err) {
			handleError(err)
		}
	}

	const deleteGuestOrder = async (id) => {
		try {
			await axiosInstance({ url: api.DELETE_GUEST_ORDERS_STORE_MANAGER, method: 'PUT', data: { id } })
			await getGuestOrders()
		} catch (err) {
			handleError(err)
		}
	}

	return (
		<UserLayout>
			{loading && (
				<Box sx={styles.loadingOverlay}>
					<Box sx={styles.loadingBackdrop} />
					<CircularProgress sx={styles.progressColor} />
				</Box>
			)}

			<Box sx={styles.container}>
				{guestOrders?.length === 0 && !loading && (
					<Card sx={styles.emptyCard}>
						<Typography sx={styles.boldText}>Chưa có dữ liệu</Typography>
					</Card>
				)}
				{guestOrders?.map((order, index) => {
					return (
						<GuestOrderItem
							key={index}
							item={order}
							handleSubmit={handleSubmit}
							setOrderEditing={setOrderEditing}
							deleteGuestOrder={deleteGuestOrder}
							index={index}
						/>
					)
				})}
			</Box>
			{orderEditing && (
				<EditGuestOrderForm closeForm={() => setOrderEditing(null)} submitOrder={createOrder} order={orderEditing} />
			)}
		</UserLayout>
	)
}

const styles = {
	loadingOverlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 9999,
	},
	loadingBackdrop: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
	},
	progressColor: {
		color: mainGreen,
		position: 'relative',
		zIndex: 1,
	},
	container: {
		maxWidth: 768,
		margin: '0 auto',
		px: 2,
		mt: '10px',
	},
	emptyCard: {
		padding: '10px',
		borderRadius: '16px',
		backgroundColor: 'white',
	},
	boldText: {
		fontWeight: 'bold',
	},
	orderCard: {
		borderRadius: '16px',
		mb: 2,
		backgroundColor: '#fff',
		boxShadow: 'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px',
		overflow: 'hidden',
	},
	orderContent: {
		p: 2,
		position: 'relative',
	},
	orderNumber: {
		position: 'absolute',
		top: 8,
		right: 8,
		fontWeight: '500',
		bgcolor: '#4CAF50',
		color: 'white',
		padding: '4px 16px',
		borderRadius: '16px',
		fontSize: '0.875rem',
	},
	contentPadding: {
		p: 2,
	},
	rowContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		py: 0.5,
	},
	rowTitle: {
		width: '250px',
		fontSize: '0.95rem',
		color: '#666',
		flexShrink: 0,
		paddingTop: '2px',
	},
	valueText: {
		flex: 1,
		color: '#333 !important',
		fontWeight: '500 !important',
		fontSize: '0.95rem',
		lineHeight: '1.5 !important',
	},
	paymentText: {
		flex: 1,
		color: '#4CAF50 !important',
		fontWeight: '600 !important',
		fontSize: '0.95rem',
	},
	deliveryNote: {
		color: '#4CAF50 !important',
		marginLeft: '8px',
		fontSize: '0.95rem',
		display: 'inline',
	},
	footer: {
		display: 'flex',
		gap: 1,
		mt: 2,
		pt: 2,
		borderTop: '1px solid #eee',
	},
	actionIconButton: {
		minWidth: 'unset',
		p: 1,
		borderRadius: '8px',
		'&:hover': {
			backgroundColor: '#f5f5f5',
		},
	},
	checkIcon: {
		color: '#4CAF50',
		fontSize: '1.2rem',
	},
	editIcon: {
		color: '#2196F3',
		fontSize: '1.2rem',
	},
	deleteIcon: {
		color: '#F44336',
		fontSize: '1.2rem',
	},
}
